import {Button, Input, Space, Table, Tag, Tooltip} from "antd";
import React, {useRef, useState} from "react";
import {EyeOutlined, SearchOutlined} from "@ant-design/icons";
import dayjs from "dayjs";
import {
    API_RESPONSE_STATUS,
    NOT_AVAILABLE,
    OPERATING_INSTRUCTIONS,
    WITHDRAW_TYPE_TAG,
    YYYY_MM_DD
} from "../../../util/Constants";
import {AccountWiseFundDetailWrapperDto} from "../../../dto/AccountWiseFundDetailWrapperDto";
import {FilterConfirmProps} from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import {InputRef} from "antd/es/input";
import {faEnvelope} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import CommonFunction from "../../../util/CommonFunction";


interface CustomerTransactionListTableProp {
    tableData: AccountWiseFundDetailWrapperDto[] | undefined
    onShowModal: (data: AccountWiseFundDetailWrapperDto) => void
    loading: boolean
    transactionType: string
}

type DataIndex = keyof AccountWiseFundDetailWrapperDto;
const TRANSACTION_TYPE_TRANSFER = "TRANSFER"

const CustomerTransactionListTable = (props: CustomerTransactionListTableProp) => {
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>();
    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };
    const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn(dataIndex);
    };
    const getColumnSearchProps = (dataIndex: DataIndex, columnName: string): {
        filterDropdown: ({
                             setSelectedKeys,
                             selectedKeys,
                             confirm,
                             clearFilters,
                             close
                         }: {
            setSelectedKeys: any;
            selectedKeys: any;
            confirm: any;
            clearFilters: any;
            close: any
        }) => JSX.Element;
        filterIcon: (filtered: boolean) => JSX.Element;
        onFilter: (value: any, record: any) => boolean;
        render: (text: any) => any;
        onFilterDropdownOpenChange: (visible: any) => void
    } => ({
        filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => (
            <div className='table-column-search' onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${columnName}`}
                    value={selectedKeys[0]}
                    onChange={(e: {
                        target: { value: React.Key; };
                    }) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{marginBottom: 12, display: 'block'}}
                />
                <div className='search-btn-row'>
                    <Button className='tbl-search-btn'
                            type="primary"
                            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                            icon={<SearchOutlined/>}
                            size="small"
                    >
                        Search
                    </Button>
                    <Button className='tbl-search-reset'
                            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
                            size="small"
                    >
                        Reset
                    </Button>
                    <Button className='tbl-search-close'
                            size="small"
                            onClick={() => {
                                close();
                            }}
                    >
                        Close
                    </Button>
                </div>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{color: filtered ? '#1677ff' : undefined}}/>
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{backgroundColor: '#ffc069', padding: 0}}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    let columns = [
        {
            title: 'Request ID',
            dataIndex: 'requestId',
            key: 'requestId',
            sorter: (a: any, b: any) => (a.requestId - b.requestId),
            ...getColumnSearchProps('requestId', 'Request ID'),
        },
        {
            title: 'CIN',
            dataIndex: 'cin',
            key: 'cin',
            sorter: (a: any, b: any) => (a.cin - b.cin),
            ...getColumnSearchProps('cin', 'CIN'),
        },
        {
            title: 'Portfolio ID',
            dataIndex: 'portFolioNumber',
            key: 'portFolioNumber',
            sorter: (a: any, b: any) => (a.portFolioNumber - b.portFolioNumber),
            ...getColumnSearchProps('portFolioNumber', 'Portfolio ID'),
        },
        {
            title: 'Customer Name',
            dataIndex: 'customerName',
            key: 'customerName',
            sorter: (a: any, b: any) => {
                a = a.customerName == null ? '' : a.customerName
                b = b.customerName == null ? '' : b.customerName
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('customerName', 'Customer Name'),
        },
        {
            title: 'Transaction By',
            dataIndex: 'backOfficeTransaction',
            key: 'backOfficeTransaction',
            align: "center",
            sorter: (a: any, b: any) => {
                if (a.backOfficeTransaction === b.backOfficeTransaction) {
                    return 0;
                } else if (a.backOfficeTransaction) {
                    return 1;
                } else {
                    return -1;
                }
            },
            render: (text: boolean) => (
                <>
                    {
                        !text ?
                            <Tag color="success">
                                CUSTOMER
                            </Tag> :
                            <Tag color="warning">
                                BACK OFFICE
                            </Tag>
                    }
                </>
            ),
            filters: [
                {
                    text: 'CUSTOMER',
                    value: false,
                },
                {
                    text: 'BACK OFFICE',
                    value: true,
                }
            ],
            onFilter: (value: string, record: any) => record.backOfficeTransaction === value,
        },
        {
            title: 'Account Type',
            dataIndex: 'accountType',
            key: 'accountType',
            align: "center",
            sorter: (a: any, b: any) => {
                a = a.accountType == null ? '' : a.accountType
                b = b.accountType == null ? '' : b.accountType
                return a.localeCompare(b);
            },
            filters: [
                {
                    text: 'OWN',
                    value: "OWN",
                }, {
                    text: 'OTHER',
                    value: 'OTHER',
                }
            ],
            onFilter: (value: string, record: any) => record.accountType.indexOf(value) === 0,
            render: (text: string) => (
                <>
                    {
                        text === "OWN" ?
                            <Tag color="success">
                                OWN
                            </Tag> :
                            <Tag color="warning">
                                OTHER
                            </Tag>
                    }


                </>
            ),
        },
        {
            title: 'Fund',
            dataIndex: 'fundName',
            key: 'fundName',
            ...getColumnSearchProps('fundName', 'Fund'),
            sorter: (a: any, b: any) => {
                a = a.fundName == null ? '' : a.fundName
                b = b.fundName == null ? '' : b.fundName
                return a.localeCompare(b);
            },
            render: (text: any) => (
                <span>{null != text ? text : NOT_AVAILABLE}</span>
            )
        },
        {
            title: 'Amount',
            dataIndex: 'fundAmount',
            key: 'fundAmount',
            align: "right",
            sorter: (a: any, b: any) => (a.fundAmount - b.fundAmount),
            render: (text: any, rec: any) => (
                <span>{null != text ? CommonFunction.currencyFormat(Math.abs(text)) : NOT_AVAILABLE}</span>
            )
        },
        {
            title: 'Full Amount Transaction',
            dataIndex: 'fullAmountSelection',
            key: 'fullAmountSelection',
            align: "center",
            sorter: (a: any, b: any) => {
                if (a.fullAmountSelection === b.fullAmountSelection) {
                    return 0;
                } else if (a.fullAmountSelection) {
                    return 1;
                } else {
                    return -1;
                }
            },
            filters: [
                {
                    text: "YES",
                    value: true,
                },
                {
                    text: "NO",
                    value: false,
                }
            ],
            onFilter: (value: string, record: any) => record.fullAmountSelection === value,
            render: (text: any, rec: any) => {
                return <span>{getFullAmountTransactionTag(text)}</span>
            }
        },
        {
            title: 'Operating Instruction',
            dataIndex: 'operatingInstruction',
            key: 'operatingInstruction',
            align: "center",
            sorter: (a: any, b: any) => {
                a = a.operatingInstruction == null ? '' : a.operatingInstruction
                b = b.operatingInstruction == null ? '' : b.operatingInstruction
                return a.localeCompare(b);
            },
            filters: [
                {
                    text: OPERATING_INSTRUCTIONS.EITHER_PARTIES_LABEL.toUpperCase(),
                    value: OPERATING_INSTRUCTIONS.EITHER_PARTIES_VALUE,
                },
                {
                    text: OPERATING_INSTRUCTIONS.SECONDARY_PARTY_LABEL.toUpperCase(),
                    value: OPERATING_INSTRUCTIONS.SECONDARY_PARTY_VALUE,
                },
                {
                    text: OPERATING_INSTRUCTIONS.PRIMARY_PARTY_LABEL.toUpperCase(),
                    value: OPERATING_INSTRUCTIONS.PRIMARY_PARTY_VALUE,
                },
                {
                    text: OPERATING_INSTRUCTIONS.BOTH_PARTIES_LABEL.toUpperCase(),
                    value: OPERATING_INSTRUCTIONS.BOTH_PARTIES_VALUE,
                }
            ],
            onFilter: (value: string, record: any) => record.operatingInstruction === value,
            render: (text: any, rec: any) => {
                return <span>{getOperatingInstructionTag(text)}</span>
            }
        },
        {
            title: 'Withdraw Type',
            dataIndex: 'transactionTag',
            key: 'transactionTag',
            align: "center",
            sorter: (a: any, b: any) => {
                a = a.transactionTag == null ? '' : a.transactionTag
                b = b.transactionTag == null ? '' : b.transactionTag
                return a.localeCompare(b);
            },
            filters: [
                {
                    text: 'ONLINE',
                    value: WITHDRAW_TYPE_TAG.WITHDRAW_MONEY,
                }, {
                    text: 'SCHEDULE',
                    value: WITHDRAW_TYPE_TAG.SCHEDULE_WITHDRAW_MONEY_SCHEDULED,
                }, {
                    text: 'SWP',
                    value: WITHDRAW_TYPE_TAG.SCHEDULE_WITHDRAW_MONEY_SWP,
                },
            ],
            onFilter: (value: string, record: any) => record.transactionTag.indexOf(value) === 0,
            render: (text: string) => (
                <>
                    {
                        text === WITHDRAW_TYPE_TAG.WITHDRAW_MONEY ?
                            <Tag color="success">
                                ONLINE
                            </Tag>
                            : text === WITHDRAW_TYPE_TAG.SCHEDULE_WITHDRAW_MONEY_SCHEDULED ?
                                <Tag color="processing">
                                    SCHEDULE
                                </Tag>
                                : <Tag color="warning">
                                    SWP
                                </Tag>
                    }


                </>
            ),
        },
        {
            title: 'Withdrawal Date',
            dataIndex: 'transactionScheduledDateTime',
            key: 'transactionScheduledDateTime',
            sorter: (a: any, b: any) => {
                a = a.transactionScheduledDateTime == null ? '' : a.transactionScheduledDateTime
                b = b.transactionScheduledDateTime == null ? '' : b.transactionScheduledDateTime
                return a.localeCompare(b);
            },
            render: (text: any) => (
                <span>{null != text ? dayjs(text).format(YYYY_MM_DD) : NOT_AVAILABLE}</span>
            )
        },
        {
            title: 'Started Date',
            dataIndex: 'startedDate',
            key: 'startedDate',
            sorter: (a: any, b: any) => {
                a = a.startedDate == null ? '' : a.startedDate
                b = b.startedDate == null ? '' : b.startedDate
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('startedDate', 'Started Date'),
            render: (text: any) => (
                <span>{null != text ? dayjs(text).format(YYYY_MM_DD) : NOT_AVAILABLE}</span>
            )
        },
        {
            title: 'Process Date',
            dataIndex: 'processDate',
            key: 'processDate',
            sorter: (a: any, b: any) => {
                a = a.processDate == null ? '' : a.processDate
                b = b.processDate == null ? '' : b.processDate
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('processDate', 'Process Date'),
            render: (text: any) => (
                <span>{null != text ? dayjs(text).format(YYYY_MM_DD) : NOT_AVAILABLE}</span>
            )
        },
        {
            title: 'API Status',
            dataIndex: 'apiResponseStatus',
            key: 'apiResponseStatus',
            align: "center",
            sorter: (a: any, b: any) => {
                a = a.apiResponseStatus == null ? '' : a.apiResponseStatus
                b = b.apiResponseStatus == null ? '' : b.apiResponseStatus
                return a.localeCompare(b);
            },
            filters: [
                {
                    text: API_RESPONSE_STATUS.PENDING,
                    value: API_RESPONSE_STATUS.PENDING,
                }, {
                    text: API_RESPONSE_STATUS.PROCESSING,
                    value: API_RESPONSE_STATUS.PROCESSING,
                }, {
                    text: API_RESPONSE_STATUS.FAILED,
                    value: API_RESPONSE_STATUS.FAILED,
                },
            ],
            onFilter: (value: string, record: any) => record.apiResponseStatus.indexOf(value) === 0,
            render: (text: string, rec: any) => (
                <>
                    {
                        text === API_RESPONSE_STATUS.PENDING ?
                            <Tag color="warning">
                                {API_RESPONSE_STATUS.PENDING}
                            </Tag>
                            : text === API_RESPONSE_STATUS.PROCESSING ?
                                <Tag color="processing">
                                    {API_RESPONSE_STATUS.PROCESSING}
                                </Tag>
                                : text === API_RESPONSE_STATUS.FAILED ?
                                    props.transactionType !== TRANSACTION_TYPE_TRANSFER ?
                                        <Tooltip placement="leftTop"
                                                 title={getFailedMessages(rec.apiFailedReason)}>
                                            <Tag color="error">
                                                {API_RESPONSE_STATUS.FAILED} <FontAwesomeIcon icon={faEnvelope}/>

                                            </Tag>
                                        </Tooltip>
                                        : <Tag color="error">
                                            {API_RESPONSE_STATUS.FAILED}

                                        </Tag>
                                    :
                                    <Tag color="default">
                                        N/A
                                    </Tag>
                    }


                </>
            ),
        },
        {
            title: 'Action',
            key: 'action',
            align: "center",
            fixed: 'right' as const,
            render: (rec: AccountWiseFundDetailWrapperDto) => (
                <Space size="middle">
                    <Button className='btn-action-outline-common btn-action-view '
                            onClick={() => showModal(rec)}
                            icon={<EyeOutlined/>}>View</Button>
                </Space>
            ),
        },
    ];

    const getOperatingInstructionTag = (value: string) => {
        switch (value) {
            case OPERATING_INSTRUCTIONS.EITHER_PARTIES_VALUE: {
                return (
                    <Tag
                        color="green" key={value}>
                        {OPERATING_INSTRUCTIONS.EITHER_PARTIES_LABEL.toUpperCase()}
                    </Tag>
                )
            }
            case OPERATING_INSTRUCTIONS.BOTH_PARTIES_VALUE: {
                return (
                    <Tag
                        color="purple" key={value}>
                        {OPERATING_INSTRUCTIONS.BOTH_PARTIES_LABEL.toUpperCase()}
                    </Tag>
                )
            }
            case OPERATING_INSTRUCTIONS.PRIMARY_PARTY_VALUE: {
                return (
                    <Tag
                        color="red" key={value}>
                        {OPERATING_INSTRUCTIONS.PRIMARY_PARTY_LABEL.toUpperCase()}
                    </Tag>
                )
            }
            case OPERATING_INSTRUCTIONS.SECONDARY_PARTY_VALUE: {
                return (
                    <Tag
                        color="blue" key={value}>
                        {OPERATING_INSTRUCTIONS.SECONDARY_PARTY_LABEL.toUpperCase()}
                    </Tag>
                )
            }
            default: {
                return (<Tag
                    color="default" key={value}>
                    {NOT_AVAILABLE.toUpperCase()}
                </Tag>)
            }
        }
    }

    const getFullAmountTransactionTag = (value: boolean) => {
        if (value){
            return (
                <Tag color="green" key={value}>
                    YES
                </Tag>
            )
        }else {
            return (
                <Tag color="red" key={value}>
                    NO
                </Tag>
            )
        }
    }

    const getFailedMessages = (data: string) => {
        if (null != data) {
            const errorList = data.split(';');

            return (
                <div>
                    <ul style={{padding: '0 10px 0 18px', width: '100%'}}>
                        {errorList.map((error, index) => (
                            <li key={index}>{error}</li>
                        ))}
                    </ul>
                </div>
            );
        }
    }
    if (props.transactionType !== "TRANSFER") {
        columns = columns.filter(column => column.key !== 'accountType');
    }
    if (props.transactionType !== "WITHDRAW") {
        columns = columns.filter(column => column.key !== 'transactionTag');
        columns = columns.filter(column => column.key !== 'transactionScheduledDateTime');
        columns = columns.filter(column => column.key !== 'fundName');
        columns = columns.filter(column => column.key !== 'fundAmount');
        columns = columns.filter(column => column.key !== 'operatingInstruction');
    }
    const showModal = (record: AccountWiseFundDetailWrapperDto) => {
        props.onShowModal(record)
    }


    return (
        <>
            <Table size='small' dataSource={props.tableData} columns={columns} scroll={{x: 'max-content'}}
                   loading={props.loading}></Table>
        </>
    )
}
export default CustomerTransactionListTable;
